import type { FC } from 'react';
import { useCallback, useEffect } from 'react';

import { useSetAtom } from 'jotai';

import { hasMicrophonePermissionAtom } from '../../atoms';

interface PermissionStateDetectorProps {
  onPermissionChange?: (hasPermission: boolean) => void;
}

const PermissionStateDetector: FC<PermissionStateDetectorProps> = ({
  onPermissionChange,
}) => {
  const setHasMicrophonePermission = useSetAtom(hasMicrophonePermissionAtom);

  const checkPermission = useCallback(async () => {
    try {
      // Try to enumerate devices to check if we have permission
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioDevices = devices.filter(
        (device) => device.kind === 'audioinput',
      );

      // Check if any audio device has a label (indicates permission granted)
      const hasPermission = audioDevices.some(
        (device) =>
          device.deviceId && device.label && device.label.trim() !== '',
      );

      if (hasPermission) {
        setHasMicrophonePermission(true);
        onPermissionChange?.(true);
      } else {
        setHasMicrophonePermission(false);
        onPermissionChange?.(false);
      }
    } catch (error) {
      console.error('Permission check failed:', error);
      setHasMicrophonePermission(false);
      onPermissionChange?.(false);
    }
  }, [setHasMicrophonePermission, onPermissionChange]);

  // Check permission on mount
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  // Listen for permission changes
  useEffect(() => {
    const handlePermissionChange = () => {
      checkPermission();
    };

    // Listen for focus events (user might have changed settings in another tab)
    window.addEventListener('focus', handlePermissionChange);

    // Listen for device changes
    navigator.mediaDevices.addEventListener(
      'devicechange',
      handlePermissionChange,
    );

    return () => {
      window.removeEventListener('focus', handlePermissionChange);
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handlePermissionChange,
      );
    };
  }, [checkPermission]);

  // Don't render anything, this is just a detector
  return null;
};

export default PermissionStateDetector;
